import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import "./App.css";
import Header from "./Header";
import "font-awesome/css/font-awesome.min.css";
import bannerImage from "./Form Banner.png";

const StudentDetails = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionStatus, setTransactionStatus] = useState({});
  const [loadingButtons, setLoadingButtons] = useState({});
  const itemsPerPage = 5;
  const maxPageButtons = 10; // Maximum number of page buttons to show
  const navigate = useNavigate();

  useEffect(() => {
    window.onbeforeunload = function () {
      sessionStorage.clear();
    };

    const loggedIn = sessionStorage.getItem("loggedIn");
    if (!loggedIn) {
      navigate("/armedforces/login");
    }

    const handlePopState = (event) => {
      window.history.pushState(null, null, window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, null, window.location.pathname);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const fetchReceiptDetails = async () => {
    const token = sessionStorage.getItem("authToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/armedforces-api/getDonationDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${token}`,
          },
        }
      );
      const result = await response.json();
      result.reverse();
      setData(result);

      // Initialize transaction statuses from the fetched data
      const initialStatuses = {};
      result.forEach((item) => {
        initialStatuses[item.transaction_id] =
          item.transaction_status || "PENDING";
      });
      setTransactionStatus(initialStatuses);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReceiptDetails();
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };
  const filteredData = data.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      const aValue = a[key] || "";
      const bValue = b[key] || "";
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setData([
      ...data.filter((row) => !filteredData.includes(row)),
      ...sortedData,
    ]);
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Calculate the range of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= maxPageButtons) {
      // Less than maxPageButtons total pages so show all
      startPage = 1;
      endPage = totalPages;
    } else {
      // More than maxPageButtons total pages so calculate start and end pages
      const maxPagesBeforeCurrent = Math.floor(maxPageButtons / 2);
      const maxPagesAfterCurrent = Math.ceil(maxPageButtons / 2) - 1;

      if (currentPage <= maxPagesBeforeCurrent) {
        // Current page near the start
        startPage = 1;
        endPage = maxPageButtons;
      } else if (currentPage + maxPagesAfterCurrent >= totalPages) {
        // Current page near the end
        startPage = totalPages - maxPageButtons + 1;
        endPage = totalPages;
      } else {
        // Current page somewhere in the middle
        startPage = currentPage - maxPagesBeforeCurrent;
        endPage = currentPage + maxPagesAfterCurrent;
      }
    }

    // Add page numbers to array
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handleViewClick = (transaction_id) => {
    const decodeID = btoa(transaction_id);
    navigate(`/armedforces/admin/donationReceipt/${decodeID}`);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem("loggedIn");
    sessionStorage.removeItem("authToken");
    navigate("/armedforces/login");
  };

  const handleRefreshClick = async (transaction_id) => {
    setLoadingButtons((prev) => ({ ...prev, [transaction_id]: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/armedforces-api/getTransactionStatus/${transaction_id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();
      const message = result.transaction_status?.extractedMessage || "Pending";

      setTransactionStatus((prev) => ({ ...prev, [transaction_id]: message }));
      setData((prev) =>
        prev.map((item) =>
          item.transaction_id === transaction_id
            ? { ...item, transaction_status: message }
            : item
        )
      );
    } catch (error) {
      console.error("Error fetching transaction status:", error);
      setTransactionStatus((prev) => ({
        ...prev,
        [transaction_id]: "Pending",
      }));
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [transaction_id]: false }));
    }
  };
  const headers = [
    { label: "Donor Name", key: "donor_name" },
    { label: "Mobile Number", key: "mobile_number" },
    { label: "Email", key: "email" },
    { label: "Amount", key: "transaction_amount" },
    { label: "Transaction ID", key: "transaction_id" },
    { label: "Transaction Status", key: "transaction_status" },
  ];

  return (
    <div className="container">
      <div className="header-section">
        <img
          src={bannerImage}
          alt="Ministry of Defence Header"
          className="banner-image"
        />
      </div>

      <div className="admin-header">
        <h2 className="admin-title">Payment Transactions</h2>
        <div className="admin-controls">
          <button type="button" className="admin-btn">
            Welcome Admin
          </button>

          <button type="button" className="signout-btn" onClick={handleSignOut}>
            <i className="fa fa-sign-out"></i> Sign Out
          </button>
        </div>
      </div>

      <div className="search-download-container">
        <input
          type="text"
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="global-search"
        />
        <CSVLink
          data={data}
          headers={headers}
          filename={"transactions.csv"}
          className="download-btn"
        >
          Download CSV
        </CSVLink>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading-spinner">
            <i className="fa fa-spinner fa-spin fa-3x"></i>
          </div>
        ) : (
          <div className="scrollable-table">
            <table className="styled-table">
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header.key}>
                      <div className="table-header">{header.label}</div>
                    </th>
                  ))}
                  <th>
                    <div className="table-header">Action</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row) => (
                  <tr key={row.transaction_id}>
                    {headers.map(({ key }) => (
                      <td key={`${row.transaction_id}-${key}`}>
                        {row[key] || "N/A"}
                      </td>
                    ))}
                    <td>
                      <div className="refresh-button-container">
                        {row.transaction_status === "SUCCESS" && (
                          <button
                            className="view-btn"
                            onClick={() => handleViewClick(row.transaction_id)}
                          >
                            View
                          </button>
                        )}
                        <button
                          className={`refresh-btn ${
                            loadingButtons[row.transaction_id] ? "loading" : ""
                          } ${
                            row.transaction_status === "SUCCESS"
                              ? "disabled"
                              : ""
                          }`}
                          onClick={() => handleRefreshClick(row.transaction_id)}
                          disabled={
                            loadingButtons[row.transaction_id] ||
                            row.transaction_status === "SUCCESS"
                          }
                        >
                          {loadingButtons[row.transaction_id] ? (
                            <i className="fa fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fa fa-refresh"></i>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredData.length > 0 && (
          <div className="pagination">
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <i className="fa fa-angle-double-left"></i>
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>

            {getPageNumbers().map((number) => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`pagination-btn ${
                  currentPage === number ? "active" : ""
                }`}
              >
                {number}
              </button>
            ))}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              <i className="fa fa-angle-double-right"></i>
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetails;
